import React, { useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useStudioWorkspace } from "../../firestoreStudio/context/StudioWorkspaceProvider";
import { useStudioProjects } from "../../firestoreStudio/hooks/useStudioProjects";

export default function ConnectionSwitchDialog({ open, connections = [], onClose }) {
  const { workspace, selectConnection } = useStudioWorkspace();
  const { data: projects = [] } = useStudioProjects();
  const [projectFilter, setProjectFilter] = useState(workspace.projectId || "");

  const activeConnections = useMemo(
    () => connections.filter((c) => !c.archived),
    [connections]
  );

  const filtered = useMemo(() => {
    if (!projectFilter) return activeConnections;
    return activeConnections.filter((c) => c.projectId === projectFilter);
  }, [activeConnections, projectFilter]);

  const handleSelect = (conn) => {
    const project = projects.find((p) => p.id === conn.projectId);
    selectConnection(conn, project?.name || workspace.projectName);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Switch Connection</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Select a connection to activate in the workspace runtime. Firestore Manager always
            follows the active workspace — it never opens a project on its own.
          </Typography>
          <TextField
            select
            size="small"
            label="Filter by project"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            fullWidth
          >
            <MenuItem value="">All projects</MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          <List dense disablePadding>
            {filtered.map((conn) => (
              <ListItemButton
                key={conn.id}
                selected={conn.id === workspace.connectionId}
                onClick={() => handleSelect(conn)}
              >
                <ListItemText
                  primary={conn.name}
                  secondary={`${conn.environment || "—"} · ${conn.firebaseProjectId || conn.projectId || "—"}`}
                />
                {conn.id === workspace.connectionId && (
                  <Chip size="small" label="Active" color="success" />
                )}
              </ListItemButton>
            ))}
            {!filtered.length && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                No connections available.
              </Typography>
            )}
          </List>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
